import { forwardRef, useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode
  description?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  className?: string
}

/** Labeled checkbox — native control tinted with the brand accent, 44px row target. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, hint, error, className = '', id: idProp, ...props },
  ref
) {
  const gid = useId()
  const id = idProp || gid
  const descId = description ? `${gid}-desc` : undefined
  const hintId = hint ? `${gid}-hint` : undefined
  const errorId = error ? `${gid}-error` : undefined
  const describedBy = [descId, hintId, errorId].filter(Boolean).join(' ') || undefined
  return (
    <label htmlFor={id} className={cn('flex items-start gap-3 min-h-tap py-1.5 cursor-pointer text-ink-body', className)}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        className="peer mt-0.5 h-5 w-5 shrink-0 rounded accent-vjal-500 focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed"
        {...props}
      />
      {label && (
        <span className="leading-snug peer-disabled:opacity-55">
          {label}
          {description && <span id={descId} className="block text-sm text-ink-muted">{description}</span>}
          {hint && !error && <span id={hintId} className="block text-xs text-ink-muted">{hint}</span>}
          {error && <span id={errorId} role="alert" className="block text-sm text-err-ink">{error}</span>}
        </span>
      )}
    </label>
  )
})
